import { createSelector } from 'reselect';
import {
  positiveMonetizationItems,
  negativeMonetizationItems,
  countPositiveMonetizationItems,
  countNegativeMonetizationItems,
} from './monetizationItem';
import { selectActiveAcquisitionOrders } from './acquisitionOrder';
import { selectUser, selectUserByRole } from './user';
import { actualCompany } from './company';
import colors from '../components/CommonStyle/colors';
import {
  getDates,
  dateIsInRange,
  addHours,
  isNullish,
} from '../helpers/sharedFunction';
import { isCallCenterOpenAt } from '../config/callCenterHours';
import moment from 'moment';

const DEFAULT_WAITING_TIME_RECALL = 2;

export const selectCallsWithStatus = (status) =>
  createSelector(
    (state) => state.Call.calls,
    (calls) => calls.filter((call) => call.status === status),
  );

export const countCallsWithStatus = (status) =>
  createSelector(
    (state) => state.Call.calls,
    (calls) => calls.filter((call) => call.status === status).length,
  );

export const selectCallsByLead = (lead) =>
  createSelector(
    (state) => state.Call.calls,
    (calls) => {
      if (!lead) return [];
      return calls.filter((call) => call.leadId === lead.id);
    },
  );

export const selectLeadCallsWithStatus = (lead, status) =>
  createSelector(selectCallsByLead(lead), (calls) => {
    status = Array.isArray(status) ? status : [status];
    return calls.filter((call) => status.includes(call.status));
  });

export const selectQueuedCalls = (filters = null) =>
  createSelector(
    (state) => state.Call.queuedCalls,
    (state) => state.Campaign.campaigns,
    (calls, campaigns) => {
      if (!filters || isNullish(filters)) return calls;
      if (filters.sector && !filters.campaignIds)
        return calls.filter((call) => call.sector === filters.sector);
      if (Array.isArray(filters.campaignIds))
        return calls.filter((call) =>
          filters.campaignIds.includes(call.campaignId),
        );
      if (filters.notArchivedCampaigns) {
        return calls.filter((call) => {
          const campaign = campaigns.find(
            (campaign) => campaign.id === call.campaignId,
          );
          return campaign && !campaign.archived;
        });
      }
      return calls;
    },
  );

export const countQueuedCalls = (filters = null) =>
  createSelector(selectQueuedCalls(filters), (calls) => calls.length);

export const selectNewQueuedCalls = (filters = null) =>
  createSelector(selectQueuedCalls(filters), (calls) =>
    calls.filter((call) => call.type === 'new'),
  );

export const countNewQueuedCalls = (filters = null) =>
  createSelector(selectNewQueuedCalls(filters), (calls) => calls.length);

export const selectRecallQueuedCalls = (filters = null) =>
  createSelector(selectQueuedCalls(filters), (calls) =>
    calls.filter((call) => call.type === 'recall'),
  );

export const countRecallQueuedCalls = (filters = null) =>
  createSelector(selectRecallQueuedCalls(filters), (calls) => calls.length);

export const selectAppointmentQueuedCalls = (filters = null) =>
  createSelector(selectQueuedCalls(filters), (calls) =>
    calls.filter((call) => call.type === 'appointment'),
  );

export const countAppointmentQueuedCalls = (filters = null) =>
  createSelector(
    selectAppointmentQueuedCalls(filters),
    (calls) => calls.length,
  );

export const countMakeRecallsNow = (filters = null) =>
  createSelector(
    actualCompany,
    selectRecallQueuedCalls(filters),
    (company, calls) =>
      calls.filter(
        (call) =>
          call.createdAt <=
          addHours(
            new Date(),
            -(company
              ? company.waitingTimeRecall || DEFAULT_WAITING_TIME_RECALL
              : DEFAULT_WAITING_TIME_RECALL),
          ),
      ).length,
  );

export const selectNextAppointments = (filters = null) =>
  createSelector(selectQueuedCalls(filters), (calls) =>
    calls.filter((call) => call.type === 'appointment'),
  );

export const selectNextPersonalAndImpersonalAppointments = (
  userId = null,
  filters = null,
) =>
  createSelector(selectNextAppointments(filters), (appointments) =>
    userId
      ? appointments.filter(
          (call) =>
            call.appointmentUserId === '' || call.appointmentUserId === userId,
        )
      : appointments,
  );

export const selectNextPersonalAppointments = (userId = null, filters = null) =>
  createSelector(
    selectNextAppointments(filters),
    selectActiveAcquisitionOrders,
    (appointments, acquisitionOrders) => {
      const acquisitionOrderIds = acquisitionOrders.map(({ id }) => id);
      appointments = appointments.filter(({ acquisitionOrderId }) =>
        acquisitionOrderIds.includes(acquisitionOrderId),
      );
      return userId
        ? appointments.filter((call) => call.appointmentUserId === userId)
        : appointments;
    },
  );

export const selectExpiredAppointments = (userId = null, filters = null) =>
  createSelector(selectNextAppointments(filters), (appointments) => {
    const expiredAppointments = appointments.filter(
      (call) => call.appointmentDate <= new Date(),
    );
    return userId
      ? expiredAppointments.filter(
          (call) =>
            call.appointmentUserId === '' || call.appointmentUserId === userId,
        )
      : expiredAppointments;
  });

export const countExpiredAppointments = (userId = null, filters = null) =>
  createSelector(
    selectExpiredAppointments(userId, filters),
    (appointments) => appointments.length,
  );

export const countMakeCallsNow = (userId = null, filters = null) =>
  createSelector(
    countNewQueuedCalls(filters),
    countMakeRecallsNow(filters),
    countExpiredAppointments(userId, filters),
    (newCalls, recallCalls, expiredAppointments) =>
      newCalls + recallCalls + expiredAppointments,
  );

export const selectNextCall = (userId = null, filters = null) =>
  createSelector(
    actualCompany,
    selectNextPersonalAndImpersonalAppointments(userId, filters),
    selectRecallQueuedCalls(filters),
    (company, appointments, recalls) => {
      const nextAppointments = appointments.sort(
        (a, b) =>
          new Date(a.appointmentDate).getTime() -
          new Date(b.appointmentDate).getTime(),
      );
      const nextAppointment = nextAppointments.length
        ? nextAppointments[0]
        : null;
      const nextRecalls = recalls.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      const nextCall = nextRecalls.length ? nextRecalls[0] : null;
      if (!nextAppointment) return nextCall;
      if (!nextCall) return nextAppointment;
      return new Date(nextAppointment.appointmentDate) <=
        addHours(
          new Date(nextCall.createdAt),
          company
            ? company.waitingTimeRecall || DEFAULT_WAITING_TIME_RECALL
            : DEFAULT_WAITING_TIME_RECALL,
        )
        ? nextAppointment
        : nextCall;
    },
  );

export const nextCallTime = (userId = null, filters = null) =>
  createSelector(selectNextCall(userId, filters), (call) => {
    if (!call) return null;
    if (call.type === 'appointment') return new Date(call.appointmentDate);
    const createdAt = new Date(call.createdAt);
    return addHours(createdAt, DEFAULT_WAITING_TIME_RECALL);
  });

export const closedCalls = (userId = null) =>
  createSelector(
    (state) => state.Call.closedCalls,
    (calls) =>
      userId ? calls.filter((call) => call.userId === userId) : calls,
  );

export const countClosedCalls = (userId = null) =>
  createSelector(closedCalls(userId), (calls) => calls.length);

export const closedCallsWithOutcome = (outcome, userId = null) =>
  createSelector(closedCalls(userId), (calls) =>
    calls.filter((call) => call.outcome === outcome),
  );

export const countClosedCallsWithOutcome = (outcome, userId = null) =>
  createSelector(
    closedCallsWithOutcome(outcome, userId),
    (calls) => calls.length,
  );

export const percentageCallAttempts = (userId = null) =>
  createSelector(
    countPositiveMonetizationItems(userId, true),
    countNegativeMonetizationItems(userId, true),
    countClosedCalls(userId),
    (positives, negatives, closed) => ((positives + negatives) / closed) * 100,
  );

export const uniqueClosedLeadCalls = (userId = null) =>
  createSelector(closedCalls(userId), (calls) => {
    let callLeadIds = [];
    let uniqueCalls = [];
    calls.forEach((call) => {
      !callLeadIds.includes(call.leadId) &&
        callLeadIds.push(call.leadId) &&
        uniqueCalls.push(call);
    });
    return uniqueCalls;
  });

export const countUniqueClosedLeadCalls = (userId = null) =>
  createSelector(uniqueClosedLeadCalls(userId), (calls) => {
    return calls.length;
  });

export const percentageSpokenCalls = (userId = null) =>
  createSelector(
    countPositiveMonetizationItems(userId, true),
    countNegativeMonetizationItems(userId, true),
    countUniqueClosedLeadCalls(userId),
    (positives, negatives, closed) => ((positives + negatives) / closed) * 100,
  );

export const firstClosedCalls = createSelector(closedCalls(), (calls) =>
  calls.filter((call) => call.count === 1 && call.type === 'new'),
);

export const averageFirstCallResponseTime = createSelector(
  firstClosedCalls,
  actualCompany,
  (calls, company) => {
    calls = calls
      .filter((call) => call.leadCreatedAt && call.startDateCall)
      // Leads that arrived with the call center closed stay out of the
      // average: nobody could work them, and counting them would say the call
      // center is slow when it was asleep.
      //
      // The hours come from the company selected in the dashboard, always one.
      // They used to come from a hardcoded table, with `new Date()` +
      // `getDay()` + `toLocaleTimeString('it-IT')`: that 'it-IT' is only the
      // format, and the clock was the BROWSER's. A CRM opened from another
      // zone computed on the wrong clock, silently.
      .filter((call) =>
        isCallCenterOpenAt(company, new Date(call.leadCreatedAt)),
      );

    const totalCalls = calls.length;
    const totalSeconds = calls.reduce((acc, call) => {
      const startDate = new Date(call.leadCreatedAt);
      const endDate = new Date(call.startDateCall);
      const seconds = Math.floor(
        (endDate.getTime() - startDate.getTime()) / 1000,
      );

      return acc + seconds;
    }, 0);

    console.log({ totalCalls, totalSeconds });

    return totalSeconds / totalCalls;
  },
);

export const drawSeriesDailyGraph = (dateRange, t, userId = null) =>
  createSelector(
    uniqueClosedLeadCalls(userId),
    positiveMonetizationItems(userId, true),
    negativeMonetizationItems(userId, true),
    (closedCalls, positives, negatives) => {
      const { startDate, endDate } = dateRange;

      const dates = getDates(startDate.toDate(), endDate.toDate());

      let dataGraph = {
        series: [
          {
            name: t('Unique Calls'),
            data: [],
            type: 'column',
            color: colors.dark,
          },
          {
            name: t('Spoken Calls'),
            data: [],
            type: 'line',
            color: colors.info,
          },
          {
            name: t('Positive'),
            data: [],
            type: 'line',
            color: colors.success,
          },
          {
            name: t('Negative'),
            data: [],
            type: 'line',
            color: colors.danger,
          },
        ],
      };

      dates.forEach((day) => {
        const startDay = moment(day).utc().startOf('day').toDate();
        const endDay = moment(day).utc().endOf('day').toDate();

        const countUniqueClosedCalls = closedCalls.filter((call) =>
          dateIsInRange(new Date(call.endDateCall), startDay, endDay),
        ).length;

        const countPositives = positives.filter((monetizationItem) =>
          dateIsInRange(new Date(monetizationItem.createdAt), startDay, endDay),
        ).length;

        const countNegatives = negatives.filter((monetizationItem) =>
          dateIsInRange(new Date(monetizationItem.createdAt), startDay, endDay),
        ).length;

        const percentageSpokenCalls = (
          ((countPositives + countNegatives) / countUniqueClosedCalls) *
          100
        ).toFixed(2);
        const percentagePositives = (
          (countPositives / countUniqueClosedCalls) *
          100
        ).toFixed(2);
        const percentageNegatives = (
          (countNegatives / countUniqueClosedCalls) *
          100
        ).toFixed(2);

        dataGraph.series[0].data.push([day.getTime(), countUniqueClosedCalls]);

        dataGraph.series[1].data.push([
          day.getTime(),
          isNaN(percentageSpokenCalls) ? 0 : percentageSpokenCalls,
        ]);

        dataGraph.series[2].data.push([
          day.getTime(),
          isNaN(percentagePositives) ? 0 : percentagePositives,
        ]);

        dataGraph.series[3].data.push([
          day.getTime(),
          isNaN(percentageNegatives) ? 0 : percentageNegatives,
        ]);
      });

      return dataGraph;
    },
  );

const chooseTimeSplit = (hour) => {
  switch (true) {
    case 8 <= hour && hour <= 9:
      return 0;
    case 10 <= hour && hour <= 11:
      return 1;
    case 12 <= hour && hour <= 13:
      return 2;
    case 14 <= hour && hour <= 15:
      return 3;
    case 16 <= hour && hour <= 17:
      return 4;
    case 18 <= hour && hour <= 19:
      return 5;
    case 20 <= hour && hour <= 21:
      return 6;
    default:
      return 6;
  }
};

export const drawSeriesCallsHourlyBar = (userId = null) =>
  createSelector(closedCalls(userId), (calls) => {
    let callsByHours = Array.from({ length: 7 }, () => []);
    calls.forEach((call) => {
      if (!call.endDateCall) return;
      const index = chooseTimeSplit(new Date(call.endDateCall).getHours());
      callsByHours[index].push(call);
    });

    const countByHours = callsByHours.map((calls) => calls.length);

    return {
      series: countByHours,
      labels: ['8-9', '10-11', '12-13', '14-15', '16-17', '18-19', '20-21'],
    };
  });

export const drawSeriesCallsWeeklyDayBar = (userId = null) =>
  createSelector(closedCalls(userId), (calls) => {
    let callsByDay = Array.from({ length: 7 }, () => []);
    calls.forEach((call) => {
      if (!call.endDateCall) return;
      const index = new Date(call.endDateCall).getDay();
      callsByDay[index].push(call);
    });

    const countByDay = callsByDay.map((calls) => calls.length);

    return {
      series: countByDay,
      labels: [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ],
    };
  });

export const calculateUserCallInsights = (roles, userId) =>
  createSelector(
    (state) => state.Call.closedCalls,
    (state) => state.MonetizationItem.monetizationItems,
    selectUserByRole(roles),
    selectUser(userId),
    (calls, monetizationItems, users, user) => {
      if (!users && !user) return [];
      users = user ? [user] : users;
      let data = [];

      for (let user of users) {
        let closedCalls = calls.filter((call) => call.userId === user.id);
        if (closedCalls.length === 0) continue;

        const positives = closedCalls.filter(
          (call) => call.outcome === 'positive',
        );
        const averageDurationPositives =
          positives
            .filter((call) => !!call.durationCall)
            .map((call) => call.durationCall)
            .reduce((a, b) => a + b, 0) /
          positives.filter((call) => !!call.durationCall).length;

        const negatives = closedCalls.filter(
          (call) =>
            call.outcome === 'negative' &&
            !['fake', 'expired'].includes(call.negativeOutcome),
        );
        const averageDurationNegatives =
          negatives
            .filter((call) => !!call.durationCall)
            .map((call) => call.durationCall)
            .reduce((a, b) => a + b, 0) /
          negatives.filter((call) => !!call.durationCall).length;

        const spokenCalls = positives.length + negatives.length;

        const fakes = closedCalls.filter(
          (call) => call.negativeOutcome === 'fake',
        );
        const averageDurationFakes =
          fakes
            .filter((call) => !!call.durationCall)
            .map((call) => call.durationCall)
            .reduce((a, b) => a + b, 0) /
          fakes.filter((call) => !!call.durationCall).length;

        const recalls = closedCalls.filter((call) => call.outcome === 'recall');
        const averageDurationRecalls =
          recalls
            .filter((call) => !!call.durationCall)
            .map((call) => call.durationCall)
            .reduce((a, b) => a + b, 0) /
          recalls.filter((call) => !!call.durationCall).length;

        const appointments = closedCalls.filter(
          (call) => call.outcome === 'appointment',
        );
        const averageDurationAppointments =
          appointments
            .filter((call) => !!call.durationCall)
            .map((call) => call.durationCall)
            .reduce((a, b) => a + b, 0) /
          appointments.filter((call) => !!call.durationCall).length;

        const revenues = monetizationItems
          .filter((monetizationItem) => monetizationItem.userId === user.id)
          .map((monetizationItem) => monetizationItem.revenue)
          .reduce((a, b) => a + b, 0);
        closedCalls = closedCalls.length;

        data.push({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
          closedCalls,
          spokenCalls,
          positives: positives.length,
          percentagePositives: (positives.length / closedCalls) * 100,
          averageDurationPositives,
          negatives: negatives.length,
          percentageNegatives: (negatives.length / closedCalls) * 100,
          averageDurationNegatives,
          percentagePositiveSpokenCalls: (positives.length / spokenCalls) * 100,
          percentageNegativeSpokenCalls: (negatives.length / spokenCalls) * 100,
          fakes: fakes.length,
          percentageFakes: (fakes.length / closedCalls) * 100,
          averageDurationFakes,
          recalls: recalls.length,
          percentageRecalls: (recalls.length / closedCalls) * 100,
          averageDurationRecalls,
          appointments: appointments.length,
          percentageAppointments: (appointments.length / closedCalls) * 100,
          averageDurationAppointments,
          revenues: revenues,
        });
      }
      return data;
    },
  );
